import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { HelpCircle, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { toast } from 'sonner';
import HelpCenterCard, { HelpCenterCardData } from './HelpCenterCard';
import HelpCenterViewModal from './HelpCenterViewModal';

interface HelpCenterCardFromDB {
  id: number;
  partner_id: string;
  title: string;
  description?: string;
  content: string;
  display_order: number;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

function ClientHelpCenter() {
  const { profile } = useAuth();
  const [cards, setCards] = useState<HelpCenterCardFromDB[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<HelpCenterCardData | null>(null);

  // Load published cards for the client's partner
  const loadCards = async () => {
    if (!profile?.partner_id) {
      setIsLoading(false);
      return;
    }

    try {
      // For clients, we need to get cards from their partner
      // Since RLS allows clients to see published cards from their partner,
      // we can use the same endpoint but it will be filtered by RLS
      const { data, error } = await supabase.functions.invoke('help-center-cards', {
        method: 'GET',
      });

      if (error) throw error;
      setCards(data?.cards || []);
    } catch (error) {
      console.error('Error loading cards:', error);
      toast.error('Failed to load help center');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCards();
  }, [profile?.partner_id]);

  // View card
  const handleViewCard = (card: HelpCenterCardData) => {
    setSelectedCard(card);
    setViewModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading help center...</span>
      </div>
    );
  }

  if (!profile?.partner_id) {
    return (
      <div className="text-center py-12">
        <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium mb-2">Help Center Not Available</h3>
        <p className="text-muted-foreground">
          Your account is not associated with a partner help center.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold mb-2">Help Center</h2>
        <p className="text-sm text-muted-foreground">
          Find answers to common questions and get help with your account.
        </p>
      </div>

      {cards.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No help articles available</h3>
            <p className="text-muted-foreground">
              Your partner hasn't published any help articles yet. Check back later or contact support.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card) => (
            <HelpCenterCard
              key={card.id}
              id={card.id}
              title={card.title}
              description={card.description}
              content={card.content}
              display_order={card.display_order}
              is_published={card.is_published}
              created_at={card.created_at}
              updated_at={card.updated_at}
              mode="client"
              onView={handleViewCard}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <HelpCenterViewModal
        isOpen={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        card={selectedCard}
        mode="client"
      />
    </div>
  );
}

export default ClientHelpCenter;